import React from 'react';
import { Flex, Stack, Text, Icon, Link, Popover, PopoverContent, PlainLink } from '@customerio/pluma-components/react';
import { FLIGHTDECK_BASE_URL } from '@/utils/flightdeck';
import type { AugmentedUser } from '@/services/auth/types';
import { type AccessSource, formatShortDate } from '../utils/constants';
import * as styles from '../index.css';

interface MasqAccessPopoverProps {
  accessSource: AccessSource;
  grantedOn?: number;
  grantedById?: string;
  grantedByEmail?: string;
  grantedUntil?: number;
  masqedInUser: AugmentedUser;
  teamAccessGranted: boolean;
  // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
  children: React.ReactElement<any>;
}

export function MasqAccessPopover({
  accessSource,
  grantedOn,
  grantedById,
  grantedByEmail,
  grantedUntil,
  masqedInUser,
  teamAccessGranted,
  children,
}: MasqAccessPopoverProps) {
  const isInternalAccessEnabled = !(masqedInUser.masqLevelNotSet || (masqedInUser.noMasqAccess && teamAccessGranted));

  const accessIcon = teamAccessGranted ? 'check-circle' : 'does-not-send';
  const accessIconClass = teamAccessGranted ? styles.accessIconSuccess : styles.accessIconCritical;
  const internalIcon = isInternalAccessEnabled ? 'check-circle' : 'does-not-send';
  const internalIconClass = isInternalAccessEnabled ? styles.accessIconSuccess : styles.accessIconCritical;

  return (
    <Popover shouldTriggerOnClick={false} shouldTriggerOnHover={true} shouldTriggerOnFocus={true}>
      {(context) => (
        <>
          {React.cloneElement(children, {
            ref: context.reference.setReference,
            ...context.reference.getReferenceProps(),
          })}
          <PopoverContent className={styles.popoverContentWidth}>
            {masqedInUser.isSuperUser ? (
              <Stack gap="100">
                <Text>You are a super-user and have full access to masquerade. Please do so with caution.</Text>
                <Text>&ldquo;With great power comes great responsibility.&rdquo;</Text>
              </Stack>
            ) : (
              <Stack gap="100">
                <Text mb="100">
                  {teamAccessGranted && isInternalAccessEnabled
                    ? 'Access requirements met:'
                    : 'Access requirements not met:'}
                </Text>

                <Flex gap="050" mb="100">
                  <span className={accessIconClass}>
                    <Icon name={accessIcon} />
                  </span>
                  <Stack gap="025">
                    {teamAccessGranted ? (
                      <>
                        <Text weight="semibold">{accessSource} access granted</Text>
                        <Text>
                          {grantedOn != null ? `on ${formatShortDate(grantedOn)}` : ''}
                          {grantedByEmail != null ? (
                            <>
                              {' by '}
                              {grantedById != null ? (
                                <Link
                                  href={`${FLIGHTDECK_BASE_URL}/users/${grantedById}/details`}
                                  isExternal
                                  variant="secondary"
                                >
                                  {grantedByEmail}
                                </Link>
                              ) : (
                                grantedByEmail
                              )}
                            </>
                          ) : null}
                          .{' '}
                          {grantedUntil === -1
                            ? 'Access granted indefinitely.'
                            : grantedUntil != null
                              ? `Access will be revoked on ${formatShortDate(grantedUntil)}.`
                              : ''}
                        </Text>
                      </>
                    ) : (
                      <>
                        <Text weight="semibold">{accessSource} access not granted</Text>
                        <Text>To request access, send a request to the account admin.</Text>
                      </>
                    )}
                  </Stack>
                </Flex>

                <Flex gap="050">
                  <span className={internalIconClass}>
                    <Icon name={internalIcon} />
                  </span>
                  <Stack gap="025">
                    {isInternalAccessEnabled ? (
                      <Text weight="semibold">Internal access approved</Text>
                    ) : (
                      <>
                        <Text weight="semibold">Internal access not approved</Text>
                        <Text>
                          This means you are not listed internally as needing access. To be added you must submit a
                          request in{' '}
                          <PlainLink
                            href="https://customerio.enterprise.slack.com/archives/C011BEBJ48J"
                            isExternal={true}
                          >
                            #access-requests
                          </PlainLink>{' '}
                          or talk to your manager.
                        </Text>
                      </>
                    )}
                  </Stack>
                </Flex>
              </Stack>
            )}
          </PopoverContent>
        </>
      )}
    </Popover>
  );
}
